package one.healthium.life.presentation.feature.today.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import ifaandroid.apps.life.composeapp.generated.resources.Res
import ifaandroid.apps.life.composeapp.generated.resources.ic_barbecue_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_bottle_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_compass_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_football_boots_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_time_6
import loco.GENERAL_STEPS
import loco.GENERAL_TODAY
import loco.LIFE_TODAY_ACTIVITY_CARD_LABEL
import loco.LIFE_TODAY_LOG_SYMPTOMS_LABEL
import loco.LIFE_TODAY_MEAL_PLAN_CARD_LABEL
import loco.LIFE_TODAY_WATER_CARD_LABEL
import loco.LIFE_TODAY_WEIGHT_CARD_LABEL
import loco.TODAY_LOGGED_SYMPTOMS_LABEL
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.dayOfWeek
import one.healthium.base.utils.relativeDate
import one.healthium.loco.resolve
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun TodayDailyControls(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onLogWeightClicked: () -> Unit,
    onLogWaterClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    onLogSymptomsClicked: () -> Unit,
) {
    Column(
        modifier = modifier.wrapContentWidth()
    ) {
        Row(
            horizontalArrangement = Arrangement.Center,
        ) {
            LogWeightControl(modifier = Modifier.weight(1f), state = state, onClick = onLogWeightClicked)
            Spacer(Modifier.width(7.dp))
            LogWaterControl(modifier = Modifier.weight(1f), state = state, onClick = onLogWaterClicked)
        }
        Spacer(Modifier.height(12.dp))
        Row(
            horizontalArrangement = Arrangement.Center,
        ) {
            MealPlanControl(modifier = Modifier.weight(1f), state = state, onClick = onMealPlanClicked)
            Spacer(Modifier.width(7.dp))
            state.todayStepsCounter?.let { steps ->
                StepsCounterControl(modifier = Modifier.weight(1f), steps)
            } ?: LogSymptomsControl(modifier = Modifier.weight(1f), state = state, onClick = onLogSymptomsClicked)
        }
        if (state.todayStepsCounter != null) {
            Spacer(Modifier.height(12.dp))
            Row(
                horizontalArrangement = Arrangement.Center,
            ) {
                LogSymptomsControl(modifier = Modifier.weight(1f), state = state, onClick = onLogSymptomsClicked)
                Spacer(Modifier.width(7.dp))
                Box(modifier = Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun LogWeightControl(modifier: Modifier = Modifier, state: TodayUiState, onClick: () -> Unit) {
    var weightValue = ""
    var dateValue: String? = null

    val latestWeightLog = state.latestWeightLog
    if (latestWeightLog != null) {
        weightValue = latestWeightLog.currentWeight.formatWeight(state.isMetric).resolve()
        dateValue = latestWeightLog.date?.relativeDate()?.resolve()?.capFirstLetter()
    }

    BaseDailyControl(
        modifier = modifier,
        title = stringResource(loco.Res.string.LIFE_TODAY_WEIGHT_CARD_LABEL),
        icon = Res.drawable.ic_compass_20,
        label = weightValue,
        date = dateValue,
        onClick = onClick,
    )
}

@Composable
private fun LogWaterControl(modifier: Modifier = Modifier, state: TodayUiState, onClick: () -> Unit) {
    val waterLabel = state.todayWaterIntake?.displayValueWithUnit
    val dateValue = state.latestWaterLog?.date?.relativeDate()?.resolve()?.capFirstLetter()

    BaseDailyControl(
        modifier = modifier,
        title = stringResource(loco.Res.string.LIFE_TODAY_WATER_CARD_LABEL),
        icon = Res.drawable.ic_bottle_20,
        label = waterLabel,
        date = dateValue,
        onClick = onClick,
    )
}

@Composable
private fun MealPlanControl(modifier: Modifier = Modifier, state: TodayUiState, onClick: (MealPlanInfoModel) -> Unit) {
    if (state.mealPlan == null) {
        return
    }
    BaseDailyControl(
        modifier = modifier,
        title = stringResource(loco.Res.string.LIFE_TODAY_MEAL_PLAN_CARD_LABEL),
        icon = Res.drawable.ic_barbecue_20,
        label = state.latestLogFoodDate?.dayOfWeek(),
        date = state.latestReadMealPlanDate?.relativeDate()?.resolve()?.capFirstLetter(),
        onClick = {
            state.mealPlan?.let(onClick::invoke)
        },
    )
}

@Composable
private fun LogSymptomsControl(modifier: Modifier = Modifier, state: TodayUiState, onClick: () -> Unit) {
    BaseDailyControl(
        modifier = modifier,
        title = stringResource(loco.Res.string.LIFE_TODAY_LOG_SYMPTOMS_LABEL),
        icon = Res.drawable.ic_barbecue_20,
        label = "${state.todayLoggedSymptoms} ${stringResource(loco.Res.string.TODAY_LOGGED_SYMPTOMS_LABEL)}",
        date = state.latestHealthLog?.date?.relativeDate()?.resolve()?.capFirstLetter(),
        onClick = onClick,
    )
}

@Composable
private fun StepsCounterControl(modifier: Modifier = Modifier, steps: DailyStepsUiModel) {
    BaseDailyControl(
        modifier = modifier,
        title = stringResource(loco.Res.string.LIFE_TODAY_ACTIVITY_CARD_LABEL),
        icon = Res.drawable.ic_football_boots_20,
        label = "${steps.steps} / ${steps.goal} ${stringResource(loco.Res.string.GENERAL_STEPS)}",
        date = stringResource(loco.Res.string.GENERAL_TODAY),
        onClick = null,
    )
}

@Composable
private fun BaseDailyControl(
    modifier: Modifier = Modifier,
    title: String,
    icon: DrawableResource,
    label: String?,
    date: String?,
    onClick: (() -> Unit)?,
) {
    Column(
        modifier = modifier
            .height(IntrinsicSize.Min)
            .defaultMinSize(minHeight = 124.dp)
            .background(AppTheme.colors.primary, shape = AppTheme.shapes.small)
            .padding(horizontal = 12.dp, vertical = 16.dp)
            .clickable(enabled = onClick != null) {
                onClick?.invoke()
            }
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                painter = painterResource(icon),
                tint = white,
                contentDescription = null,
            )
            Spacer(Modifier.width(6.dp))
            Text(
                text = title,
                style = AppTheme.textStyles.secondary_16_400,
                color = AppTheme.colors.onPrimary,
            )
            if (onClick != null) {
                Spacer(Modifier.width(6.dp))
                Image(
                    modifier = Modifier.height(8.dp),
                    painter = painterResource(AppIcons.icArrowRight),
                    contentDescription = null,
                )
            }
        }
        if (label != null) {
            Spacer(Modifier.height(8.dp))
            Text(
                modifier = Modifier
                    .background(color = AppTheme.colors.secondary, shape = RoundedCornerShape(4.dp))
                    .padding(horizontal = 12.dp, vertical = 4.dp),
                text = label,
                style = AppTheme.textStyles.secondary_9_400,
                color = AppTheme.colors.onSecondary
            )
        }
        Spacer(Modifier.weight(1f))
        if (date != null) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Image(
                    painter = painterResource(Res.drawable.ic_time_6),
                    contentDescription = null,
                )
                Spacer(Modifier.width(4.dp))
                Text(
                    text = date,
                    style = AppTheme.textStyles.secondary_9_400,
                    color = AppTheme.colors.onPrimary,
                )
            }
        }
    }
}
